// Help-desk HTTP client. The same /help-requests endpoints serve both the
// account support messenger (components/help/ in the authenticated layout)
// and the admin /admin/helpdesk page — they only differ in which JWT is sent. The web app
// uses two separate localStorage tokens (`tt_token` for accounts,
// `tt_admin_token` for admins), so we expose two parallel clients backed by
// the shared auth-fetch factory (see lib/auth-fetch.ts) — each binds the
// audience config that owns the appropriate token + refresh behavior.

import type {
  HelpRequestStatus,
  HelpRequestListItem,
  HelpRequestDetail,
  HelpMessage,
} from '@tenfour/contracts'
import {
  createAuthFetcher,
  CONTRACTOR_AUDIENCE,
  ADMIN_AUDIENCE,
} from '@/lib/auth-fetch'

function buildClient(fetcher: { api: <T>(path: string, options?: RequestInit) => Promise<T> }) {
  const { api } = fetcher
  return {
    list(status?: HelpRequestStatus): Promise<HelpRequestListItem[]> {
      const qs = status ? `?status=${encodeURIComponent(status)}` : ''
      return api<HelpRequestListItem[]>(`/help-requests${qs}`)
    },
    get(id: string): Promise<HelpRequestDetail> {
      return api<HelpRequestDetail>(`/help-requests/${id}`)
    },
    create(body: { subject: string; body: string; topic?: string }): Promise<HelpRequestDetail> {
      return api<HelpRequestDetail>('/help-requests', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    addMessage(id: string, body: string): Promise<HelpMessage> {
      return api<HelpMessage>(`/help-requests/${id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      })
    },
    updateStatus(id: string, status: HelpRequestStatus): Promise<HelpRequestDetail> {
      return api<HelpRequestDetail>(`/help-requests/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
    },
  }
}

export const accountHelpApi = buildClient(createAuthFetcher(CONTRACTOR_AUDIENCE))
export const adminHelpApi = buildClient(createAuthFetcher(ADMIN_AUDIENCE))
